import type { ImpactResponse, ImpactSummary } from '@tabel/shared/schemas/rules';
import { ChartColumn } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { errorText } from '../../components/ErrorToast';
import { Button, Skeleton } from '../../components/ui';
import { api } from '../../lib/api';
import { ApiError, reportClientError } from '../../lib/errors';
import { DaysDialog } from './DaysDialog';
import { shouldReport } from './LoadError';
import { LABEL, T, say } from './texts';

/*
 * Строки влияния под примером (004, T065; contracts/ui.md, «Изменение», «Правила запросов»; contracts/impact.md;
 * FR-023): «Это изменение» — правка открытой карточки, «Весь черновик» — все изменения черновика с этой правкой.
 * Влияние считает поток движка на сервере по неподписанным дням с даты начала; интерфейс только показывает числа.
 * Запрос — через 400 мс после последнего изменения, с номером `token`: ответ на старый номер не показывается,
 * `IMPACT_SUPERSEDED` (расчёт снят новым запросом) — не ошибка.
 */

/** Сколько ждать после последнего изменения значения, прежде чем считать влияние. */
export const IMPACT_DELAY_MS = 400;

/** Что считать: от чего строится кандидат, правка правила, черновик и дата начала окна. */
export interface ImpactInput {
  readonly base: 'version' | 'draft';
  readonly group: string;
  readonly values: Readonly<Record<string, unknown>>;
  readonly draftEtag?: string;
  readonly validFrom?: string;
}

export type ImpactState =
  | { readonly status: 'idle' }
  | { readonly status: 'computing' }
  | { readonly status: 'done'; readonly result: ImpactResponse }
  | { readonly status: 'error'; readonly error: unknown; readonly retry: () => void };

/**
 * Влияние правки: `null` — считать нечего (значения не изменены). Каждое изменение `req` откладывает запрос на
 * 400 мс; ответ принимается, только если его номер — последний.
 */
export function useImpact(req: ImpactInput | null): ImpactState {
  const key = req ? JSON.stringify(req) : null;
  const [state, setState] = useState<ImpactState>(key ? { status: 'computing' } : { status: 'idle' });
  const [again, setAgain] = useState(0);
  const seq = useRef(0);
  const prefix = useRef(Math.random().toString(36).slice(2, 10));

  // biome-ignore lint/correctness/useExhaustiveDependencies: запрос зависит только от ключа и «Повторить»
  useEffect(() => {
    const mine = ++seq.current;
    if (key === null) {
      setState({ status: 'idle' });
      return;
    }
    setState({ status: 'computing' });
    const body = JSON.parse(key) as ImpactInput;
    const timer = setTimeout(() => {
      const token = `${prefix.current}-${mine}`;
      api.impact({ ...body, values: { ...body.values }, token }).then(
        (result) => {
          if (seq.current === mine) setState({ status: 'done', result });
        },
        (error: unknown) => {
          if (seq.current !== mine) return;
          if (error instanceof ApiError && error.code === 'IMPACT_SUPERSEDED') return;
          if (shouldReport(error)) reportClientError('Rules', 'impact', error);
          setState({ status: 'error', error, retry: () => setAgain((n) => n + 1) });
        },
      );
    }, IMPACT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [key, again]);

  return state;
}

/** Строка влияния: «Это изменение: 3 дня у 2 человек, +0,75 ч, вопросов бригадирам +0 −1; в днях с правкой — 1; …». */
export function impactLine(label: string, s: ImpactSummary): string {
  const errors = s.errorDays ?? 0;
  const nothingElse = s.changedDays === 0 && s.questionsPlus === 0 && s.questionsMinus === 0;
  // Только дни с ошибкой расчёта — без «часы не изменятся»: правила этот день не считают (T106).
  if (errors > 0 && nothingElse && s.editedDays === 0) return `${label}: ${say.impactErrors(errors)}`;
  let line = say.impactMain(label, s);
  if (s.questionsPlus > 0 || s.questionsMinus > 0)
    line += `, ${say.impactQuestions(s.questionsPlus, s.questionsMinus)}`;
  if (s.editedDays > 0) line += `; ${say.impactEdited(s.editedDays)}`;
  if (errors > 0) line += `; ${say.impactErrors(errors)}`;
  if (s.signedDays > 0) line += `; ${T.impactSigned}`;
  return line;
}

/** Ни часы, ни вопросы, ни дни с правкой или ошибкой не меняются. */
const quiet = (s: ImpactSummary) =>
  s.changedDays === 0 &&
  s.questionsPlus === 0 &&
  s.questionsMinus === 0 &&
  s.editedDays === 0 &&
  (s.errorDays ?? 0) === 0;

const BOX =
  'flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[10px] border border-dashed border-line-strong px-3 py-2.5 text-[13.5px]';

export function ImpactLines({
  state,
  showWhole,
  effect,
}: {
  state: ImpactState;
  showWhole: boolean;
  /** Что меняет правило, если не часы дней: «часы дней не изменятся. Правило меняет …». */
  effect?: string;
}) {
  const [days, setDays] = useState(false);
  if (state.status === 'idle') {
    return (
      <div className={BOX}>
        <ChartColumn size={16} strokeWidth={1.75} className="shrink-0 text-ink-3" aria-hidden />
        <p className="min-w-0 flex-1 text-ink-2">{T.editHint}</p>
      </div>
    );
  }
  if (state.status === 'computing') {
    return (
      <div aria-busy="true" aria-label={LABEL.computing} className={BOX}>
        <ChartColumn size={16} strokeWidth={1.75} className="shrink-0 text-ink-3" aria-hidden />
        <span className="text-ink-2">{LABEL.computing}</span>
        <Skeleton className="h-3 min-w-24 flex-1" />
      </div>
    );
  }
  if (state.status === 'error') {
    const id = state.error instanceof ApiError ? state.error.requestId : null;
    const why = errorText(state.error, '');
    return (
      <div role="alert" className={BOX}>
        <p className="min-w-0 flex-1 text-bad">
          {why ? `${T.impactError} ${why}.` : T.impactError}
          {id && <span className="block text-[12px] text-ink-3">{say.errorCode(id)}</span>}
        </p>
        <Button size="sm" onClick={state.retry}>
          {LABEL.retry}
        </Button>
      </div>
    );
  }
  // Ошибка проверки значений: с таким набором дни не считаются — вместо «N дней с ошибкой расчёта» причина (приёмка
  // 16.10.2026: «81 день с ошибкой» при одном неверном поле).
  // Ответ не разбирается схемой (как `checksOf`): `errors` может не прийти.
  if (Array.isArray(state.result.errors) && state.result.errors.length > 0) {
    return (
      <div className={BOX}>
        <ChartColumn size={16} strokeWidth={1.75} className="shrink-0 text-ink-3" aria-hidden />
        <p className="min-w-0 flex-1 text-ink-2">{T.impactBlocked}</p>
      </div>
    );
  }
  const { thisChange, wholeDraft } = state.result;
  return (
    <div className={BOX}>
      <ChartColumn
        size={16}
        strokeWidth={1.75}
        className="shrink-0 self-start text-ink-3 sm:mt-0.5"
        aria-hidden
      />
      <div className="min-w-0 flex-1 space-y-0.5">
        <p>
          {effect !== undefined && quiet(thisChange.summary)
            ? say.impactNotHours(LABEL.impactThis, effect)
            : impactLine(LABEL.impactThis, thisChange.summary)}
        </p>
        {(thisChange.summary.zeroedDays ?? 0) > 0 && (
          <p className="font-medium text-warn">{say.impactZeroed(thisChange.summary.zeroedDays)}</p>
        )}
        {showWhole && wholeDraft && <p>{impactLine(LABEL.impactWhole, wholeDraft.summary)}</p>}
      </div>
      {thisChange.days.length > 0 && (
        <Button size="sm" variant="ghost" onClick={() => setDays(true)}>
          {LABEL.whichDays}
        </Button>
      )}
      <DaysDialog open={days} days={thisChange.days} onClose={() => setDays(false)} />
    </div>
  );
}
